import { useState, useEffect } from 'react';
import { handleApiError } from '../../utils/errorHandler';
import { formatCurrency } from '../../utils/currencyUtils';
import OmButton from '../ui/OmButton';
import OmTextField from '../ui/OmTextField';
import OmAlert from '../ui/OmAlert';

const RecommendationModal = ({ 
  show, 
  onClose, 
  onRecommend, 
  isRecommending,
  recommendationNotes,
  setRecommendationNotes,
  claim,
  onBenefitApprove,
  isApprovingBenefit
}) => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(1); // 1: Benefit amount, 2: Recommendation
  const [approvedAmount, setApprovedAmount] = useState('');
  const [justification, setJustification] = useState('');

  // Reset error and success when modal opens/closes
  useEffect(() => {
    if (show) {
      setError(null);
      setSuccess(false);
      
      // Check if benefit amount is already approved - skip to step 2
      if (claim?.status === 'BENEFIT_PENDING_APPROVAL') {
        setStep(2); // Jump directly to recommendation step
      } else {
        setStep(1); // Start with benefit approval step
      }
      
      setApprovedAmount('');
      setJustification('');
    }
  }, [show, claim?.status]);

  // Reset error when notes change
  useEffect(() => {
    if (error) setError(null);
  }, [recommendationNotes, error]);

  // Calculate suggested amount based on rules engine
  const getSuggestedAmount = () => {
    if (claim?.suggestedBenefitAmount) {
      return claim.suggestedBenefitAmount;
    }

    // Fallback calculation based on claim type and sum assured
    const sumAssured = claim?.sumAssured || 0;
    switch (claim?.type) {
      case 'DTH':
      case 'DEATH':
        return sumAssured; // 100% of Sum Assured
      case 'PTD':
      case '004': // PTD business code
        return sumAssured; // 100% of Capital Sum Assured
      case 'TTD':
      case '006': // TTD business code
        // 10% per month for max 6 months
        const duration = Math.min(claim?.duration || 1, 6);
        return sumAssured * 0.1 * duration;
      case '002':
      case 'CRITICAL_ILLNESS':
        // Variable based on condition - default to 75% if not specified
        return sumAssured * 0.75;
      case 'DISABILITY':
        // Generic disability - check subtype
        if (claim.disabilityType === 'PTD') {
          return sumAssured; // 100% of CSA
        } else if (claim.disabilityType === 'TTD') {
          const duration = Math.min(claim?.duration || 1, 6);
          return sumAssured * 0.1 * duration;
        }
        return 0; // Claims Officer must enter amount
      case '010': // Spousal Cover
      case '012': // Coffin Cover
      case '013': // Parent Cover
      case '005': // Medical Claims
        return 0; // No defined rules - Claims Officer enters amount
      default:
        return 0; // Claims Officer must enter amount for undefined types
    }
  };

  const getAppliedRule = () => {
    if (claim?.appliedRule) {
      return claim.appliedRule;
    }

    // Fallback rule description based on claim type
    switch (claim?.type) {
      case 'DTH':
      case 'DEATH':
        return '100% of Sum Assured (Standard Death Benefit)';
      case 'PTD':
      case '004': // PTD business code
        return '100% of Capital Sum Assured (Permanent Total Disability)';
      case 'TTD':
      case '006': // TTD business code
        const duration = Math.min(claim?.duration || 1, 6);
        return `10% of CSA per month × ${duration} months (Temporary Total Disability)`;
      case '002':
      case 'CRITICAL_ILLNESS':
        return 'Variable percentage based on condition severity (Critical Illness)';
      case 'DISABILITY':
        // Generic disability - check subtype
        if (claim.disabilityType === 'PTD') {
          return '100% of Capital Sum Assured (Permanent Total Disability)';
        } else if (claim.disabilityType === 'TTD') {
          const duration = Math.min(claim?.duration || 1, 6);
          return `10% of CSA per month × ${duration} months (Temporary Total Disability)`;
        }
        return 'Claims Officer to determine appropriate amount';
      case '010':
        return 'Spousal Cover - Claims Officer to determine amount';
      case '012':
        return 'Coffin Cover - Claims Officer to determine amount';
      case '013':
        return 'Parent Cover - Claims Officer to determine amount';
      case '005':
        return 'Medical Claims - Claims Officer to determine reimbursement amount';
      default:
        return 'Claims Officer to determine appropriate amount';
    }
  };

  useEffect(() => {
    if (show) {
      const suggested = getSuggestedAmount();
      if (claim?.suggestedBenefitAmount !== undefined && claim.suggestedBenefitAmount !== null) {
        setApprovedAmount(claim.suggestedBenefitAmount.toString());
      } else if (suggested !== undefined && suggested !== null) {
        setApprovedAmount(suggested.toString());
      } else if (claim?.benefitAmount !== undefined && claim.benefitAmount !== null) {
        setApprovedAmount(claim.benefitAmount.toString());
      } else {
        setApprovedAmount('');
      }
    }
  }, [show, claim]);

  if (!show) return null;

  const handleRecommend = async () => {
    if (!recommendationNotes || recommendationNotes.trim().length < 10) {
      setError('Please provide detailed recommendation notes (minimum 10 characters)');
      return;
    }

    try {
      await onRecommend();
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      const errorInfo = handleApiError(err);
      setError(errorInfo.message);
    }
  };

  const isAmountChanged = () => {
    const numericAmount = parseFloat(approvedAmount);
    const suggested = getSuggestedAmount();
    
    // If no suggested amount (undefined or null), no change comparison needed
    if (suggested === undefined || suggested === null) {
      return false;
    }
    
    return Math.abs(numericAmount - suggested) > 0.01;
  };

  const handleApproveBenefit = async () => {
    if (!approvedAmount || isNaN(parseFloat(approvedAmount)) || parseFloat(approvedAmount) < 0) {
      setError('Please enter a valid benefit amount (0 or greater)');
      return;
    }
    try {
      await onBenefitApprove({
        claimId: claim.id,
        approvedBenefitAmount: parseFloat(approvedAmount),
        justification: justification.trim() || undefined
      });
      setStep(2);
      setError(null);
    } catch (err) {
      const errorInfo = handleApiError(err);
      setError(errorInfo.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-carbon-black bg-opacity-60 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-0 border border-steel/20 w-full max-w-2xl shadow-depth bg-crystal-white rounded-lg overflow-hidden">
        <div className="card-header">
          <h3 className="text-lg font-display font-semibold text-crystal-white">
            {step === 1 ? 'Approve Benefit Amount' : 'Recommend Claim for Approval'}
          </h3>
        </div>
        
        <div className="card-body space-y-6">
          {error && (
            <OmAlert
              alertType="error"
              message={error}
              showActions={false}
            />
          )}

          {success && (
            <OmAlert
              alertType="success"
              message="Claim recommended for approval successfully!"
              showActions={false}
            />
          )}

          {step === 1 && (
            <>
              {/* Claim Summary */}
              <div className="bg-silver-mist/10 p-4 rounded-lg">
                <h4 className="text-sm font-semibold text-deep-jungle mb-3">Claim Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-steel font-medium">Claimant:</span>
                    <span className="ml-2 text-graphite">{claim?.claimantName}</span>
                  </div>
                  <div>
                    <span className="text-steel font-medium">Type:</span>
                    <span className="ml-2 text-graphite">{claim?.type?.replace('_', ' ')}</span>
                  </div>
                  <div>
                    <span className="text-steel font-medium">Sum Assured:</span>
                    <span className="ml-2 text-graphite font-semibold">
                      {formatCurrency(claim?.sumAssured || 0)}
                    </span>
                  </div>
                  <div>
                    <span className="text-steel font-medium">Current Benefit:</span>
                    <span className="ml-2 text-graphite">
                      {formatCurrency(claim?.benefitAmount || 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Rules Engine Calculation */}
              <div className="bg-fresh-leaf/5 border border-fresh-leaf/20 p-4 rounded-lg">
                <h4 className="text-sm font-semibold text-woodland-moss mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Rules Engine Calculation
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-steel font-medium">Applied Rule:</span>
                    <span className="text-woodland-moss text-sm">{getAppliedRule()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-steel font-medium">Suggested Amount:</span>
                    <span className="text-woodland-moss font-bold text-lg">
                      {getSuggestedAmount() !== undefined && getSuggestedAmount() !== null
                        ? formatCurrency(getSuggestedAmount())
                        : 'To be determined by Claims Officer'
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Benefit Amount Input */}
              <div>
                <OmTextField
                  label="Approved Benefit Amount"
                  value={approvedAmount}
                  onChange={(e) => setApprovedAmount(e.target.value)}
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Enter approved amount..."
                  disabled={isApprovingBenefit}
                  required
                  customValidationMessage={
                    !approvedAmount || isNaN(parseFloat(approvedAmount)) || parseFloat(approvedAmount) < 0
                      ? "Please enter a valid benefit amount (0 or greater)"
                      : ""
                  }
                  alwaysShowValidationMessage={
                    !!(!approvedAmount || isNaN(parseFloat(approvedAmount)) || parseFloat(approvedAmount) < 0)
                  }
                  supportingText={
                    (() => {
                      const suggested = getSuggestedAmount();
                      if (suggested === undefined || suggested === null) {
                        return "No automatic calculation available - enter appropriate amount";
                      }
                      return isAmountChanged()
                        ? `Amount differs from suggested: ${formatCurrency(suggested)}`
                        : "Amount matches suggested calculation";
                    })()
                  }
                />
              </div>

              {/* Justification - Required if amount is changed */}
              {isAmountChanged() && (
                <div>
                  <OmTextField
                    label="Justification (Required for amount changes)"
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    rows={3}
                    placeholder="Please provide justification for the amount change..."
                    disabled={isApprovingBenefit}
                    required
                    customValidationMessage={
                      isAmountChanged() && !justification.trim()
                        ? "Justification is required when changing the suggested amount"
                        : ""
                    }
                    alwaysShowValidationMessage={!!(isAmountChanged() && !justification.trim())}
                    supportingText={`Characters: ${justification.length}`}
                  />
                </div>
              )}

              {/* Optional justification if amount is not changed */}
              {!isAmountChanged() && (
                <div>
                  <OmTextField
                    label="Additional Notes (Optional)"
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    rows={2}
                    placeholder="Add any additional notes about this approval..."
                    disabled={isApprovingBenefit}
                    supportingText={`Characters: ${justification.length}`}
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-steel/10">
                <OmButton
                  onClick={onClose}
                  buttonType="outlined-button"
                  buttonSize="medium"
                  leadingIcon="arrow_back"
                  disabled={isApprovingBenefit}
                >
                  Back to Claims List
                </OmButton>
                <OmButton
                  onClick={handleApproveBenefit}
                  buttonType="filled-button"
                  buttonSize="medium"
                  leadingIcon="monetization_on"
                  disabled={
                    !approvedAmount ||
                    isNaN(parseFloat(approvedAmount)) ||
                    parseFloat(approvedAmount) < 0 ||
                    (isAmountChanged() && !justification.trim()) ||
                    isApprovingBenefit
                  }
                >
                  {isApprovingBenefit ? 'Approving…' : 'Continue to Recommendation'}
                </OmButton>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              {/* Show benefit amount status if already approved */}
              {claim?.status === 'BENEFIT_PENDING_APPROVAL' && (
                <div className="bg-green-50 border border-green-200 p-4 rounded mb-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-semibold text-green-800">Benefit Amount Already Approved</h4>
                      <p className="text-sm text-green-700 mt-1">
                        Approved Amount: <strong>{formatCurrency(claim?.benefitAmount || 0)}</strong>
                        {claim?.benefitApprovalNotes && (
                          <span className="block mt-1">
                            Approval Notes: <em>"{claim.benefitApprovalNotes}"</em>
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 p-4 rounded">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-semibold text-blue-800">Claims Officer Recommendation</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      By recommending this claim, you certify that all documents have been reviewed, 
                      medical stamps validated, and the claim meets approval criteria.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <OmTextField
                  label="Recommendation Notes *"
                  value={recommendationNotes}
                  onChange={(e) => setRecommendationNotes(e.target.value)}
                  rows={6}
                  placeholder="Provide detailed notes explaining your recommendation. Include verification status, medical stamp validation, and any other relevant observations..."
                  disabled={isRecommending || success}
                  required
                  customValidationMessage={
                    recommendationNotes.length > 0 && recommendationNotes.trim().length < 10
                      ? "Minimum 10 characters required for detailed notes"
                      : !recommendationNotes.trim() 
                        ? "Recommendation notes are required"
                        : ""
                  }
                  alwaysShowValidationMessage={!!(
                    (!recommendationNotes.trim() || recommendationNotes.trim().length < 10)
                  )}
                  supportingText={`Characters: ${recommendationNotes.length} / 10 minimum`}
                />
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-steel/10">
                {/* Only show back button if benefit isn't already approved */}
                {claim?.status !== 'BENEFIT_PENDING_APPROVAL' ? (
                  <OmButton
                    onClick={() => setStep(1)}
                    buttonType="outlined-button"
                    buttonSize="medium"
                    leadingIcon="arrow_back"
                    disabled={isRecommending || success}
                  >
                    Back
                  </OmButton>
                ) : (
                  <div></div> // Empty div to maintain layout spacing
                )}
                <div className="flex space-x-3">
                  <OmButton
                    onClick={onClose}
                    buttonType="outlined-button"
                    buttonSize="medium"
                    disabled={isRecommending || success}
                  >
                    Cancel
                  </OmButton>
                  <OmButton
                    onClick={handleRecommend}
                    buttonType="filled-button"
                    buttonSize="medium"
                    leadingIcon="check"
                    disabled={
                      !recommendationNotes || 
                      recommendationNotes.trim().length < 10 ||
                      isRecommending || 
                      success
                    }
                  >
                    {isRecommending ? 'Recommending…' : 'Recommend for Approval'}
                  </OmButton>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendationModal;
